<script setup lang="ts">
import { useCurrentUser } from "@/api/current-user";
import ConfigSection from "@/layouts/content/ConfigSection.vue";
import { useAuthStore, useSnackbarStore } from "@/stores";
import { useForm } from "@soft-db/form";
import { Button } from "@soft-db/ui";
import { useI18n } from "vue-i18n";
import PasswordField from "../common/PasswordField.vue";

type PasswordForm = {
	oldPassword: string;
	newPassword: string;
};

const { data: user } = useCurrentUser();
const form = useForm<PasswordForm>();

const oldPassword = form.useField("oldPassword", { isRequired: true });
const newPassword = form.useField("newPassword", { isRequired: true, min: 8 });

const snackbarStore = useSnackbarStore();

const authStore = useAuthStore();

const updatePassword = form.handleSubmit(async (data) => {
	const result = await authStore.changePassword(data.oldPassword, data.newPassword);

	if (result) {
		snackbarStore.openOnSaved();
	} else {
		snackbarStore.openOnError(t("snackbar.passwordMismatch"));
	}

	form.clear();
});

const { t } = useI18n();

const emailOrUsername = computed<string | null>(() => {
	if (user.value == null) {
		return null;
	}

	const { auth } = user.value;
	switch (auth.type) {
		case "username":
			return auth.username;
		case "email":
			return auth.email;
		case "public":
			return null;
	}
});
</script>

<template>
	<form @submit="updatePassword">
		<ConfigSection
			:title="$t('profile.passwordUpdate')"
			:description="t('profile.passwordUpdateCaption')"
		>
			<!--   For better auto completion of browser   -->
			<input :value="emailOrUsername" autocomplete="username" hidden />
			<PasswordField
				v-bind="oldPassword.attrs"
				:label="t('profile.currentPassword')"
				autocomplete="password"
			/>
			<PasswordField
				:label="t('profile.newPassword')"
				autocomplete="new-password"
				v-bind="newPassword.attrs"
			/>

			<Button type="submit" color="primary" class="mt-4! ml-auto w-fit">
				{{ $t("profile.savePassword") }}
			</Button>
		</ConfigSection>
	</form>
</template>
